import { useLayoutEffect, useRef, useState, type Ref } from 'react';

/**
 * Auto-breites Inline-Eingabefeld für den „Lückentext"-Vorlagen-Dialog: sitzt mitten im
 * Fließtext und wächst mit dem Inhalt. Robust ohne `field-sizing` (das ist noch nicht überall
 * verfügbar): ein unsichtbarer Mess-Span mit identischer Typografie reserviert die Breite im
 * Fluss, das eigentliche `<input>` liegt deckungsgleich darüber.
 */
interface InlineFillProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** INPUT-Stelle (manuell auszufüllen) optisch hervorheben (amber). Sonst CONTACT-Look (accent). */
  highlight?: boolean;
  title?: string;
  ariaLabel?: string;
  inputRef?: Ref<HTMLInputElement>;
  /** Optional: Aktion bei Enter (z.B. Senden). Ohne Handler tut Enter im Inline-Feld nichts. */
  onEnter?: () => void;
}

export function InlineFill({
  value,
  onChange,
  placeholder = '',
  highlight = false,
  title,
  ariaLabel,
  inputRef,
  onEnter,
}: InlineFillProps) {
  const mirrorRef = useRef<HTMLSpanElement | null>(null);
  const [width, setWidth] = useState(0);

  // Breite des unsichtbaren Mess-Spans (gleiche Schrift/Padding wie das Input) auf das Input legen.
  useLayoutEffect(() => {
    if (mirrorRef.current) setWidth(mirrorRef.current.offsetWidth);
  }, [value, placeholder]);

  // Mess-Inhalt: aktueller Wert, sonst Placeholder, mind. ein paar Zeichen Breite.
  const mirrorText = value.length > 0 ? value : placeholder || '   ';

  return (
    <span className="relative inline-block align-baseline">
      <span
        ref={mirrorRef}
        aria-hidden
        className="invisible whitespace-pre px-1 text-sm font-medium"
      >
        {mirrorText}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={value}
        title={title}
        aria-label={ariaLabel ?? placeholder}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Enter darf in einem Inline-Feld nicht versehentlich abschicken (kein implizites
          // Form-Submit) — nur die explizit gewünschte Aktion, falls ein Handler übergeben wurde.
          if (e.key === 'Enter') {
            e.preventDefault();
            onEnter?.();
          }
        }}
        style={{ width: Math.max(width, 12) }}
        className={[
          'absolute inset-0 rounded-field border-b-2 px-1 text-sm font-medium transition-colors focus:outline-none',
          highlight
            ? 'border-amber-400 bg-amber-50 text-amber-900 placeholder:text-amber-700/50 focus:bg-amber-50'
            : 'border-kalkuel-accent bg-kalkuel-accent-soft text-kalkuel-primary placeholder:text-kalkuel-muted focus:bg-white',
        ].join(' ')}
      />
    </span>
  );
}
